import { createPortal } from "react-dom";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { FullscreenModal, Button, CalendarInput, DropFileInput } from "shared";
import Input from "shared/Input/Input";
import {
  useCloseAsset,
  useUploadInvestmentFile,
  useAttachInvestmentFile,
} from "hooks/investments";
import { useGetMe } from "hooks/auth";
import "./CloseAssetModal.scss";

interface CloseAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: any | null;
}

interface FormValues {
  salePrice: string;
  closedAt: string;
  contractFile: File | null;
}

const validationSchema = Yup.object({
  salePrice: Yup.number()
    .required("Cena sprzedaży jest wymagana")
    .positive("Cena musi być dodatnia"),
  closedAt: Yup.string().required("Data sprzedaży jest wymagana"),
  contractFile: Yup.mixed().nullable(),
});

const initialValues: FormValues = {
  salePrice: "",
  closedAt: "",
  contractFile: null,
};

// Mapowanie kodów walut na symbole
const getCurrencySymbol = (currencyCode: string): string => {
  const symbols: Record<string, string> = {
    PLN: "zł",
    USD: "$",
    EUR: "€",
    GBP: "£",
    CHF: "CHF",
    JPY: "¥",
  };
  return symbols[currencyCode] || currencyCode;
};

const CloseAssetModal = ({ isOpen, onClose, item }: CloseAssetModalProps) => {
  const { mutateAsync: closeAsset, isPending } = useCloseAsset();
  const { mutateAsync: uploadFile } = useUploadInvestmentFile();
  const { mutateAsync: attachFile } = useAttachInvestmentFile();
  const { data: me } = useGetMe();

  const handleSubmit = async (values: FormValues) => {
    if (!item?.uuid) return;

    try {
      // Zamknij aktywo
      await closeAsset(
        {
          uuid: item.uuid,
          closedAt: values.closedAt,
          salePrice: Number(values.salePrice),
          currencyUuid: item.currency?.uuid,
        },
        {
          onSuccess: async (data) => {
            // Upload pliku umowy, jeśli został dodany
            if (values.contractFile instanceof File) {
              try {
                const userId: string | number =
                  (me as any)?.user?.id ?? (me as any)?.id ?? "";

                const uploaded = await uploadFile({
                  file: values.contractFile,
                  type: "attachment",
                  parentId: userId,
                });

                const fileUuid = Array.isArray(uploaded)
                  ? uploaded[0]?.uuid
                  : (uploaded as any)?.uuid;

                if (fileUuid && item.uuid) {
                  await attachFile({
                    investmentUuid: item.uuid,
                    fileUuid,
                    role: "closure_contract",
                  });
                }
              } catch {
                // Plik nie blokuje zamknięcia aktywa
              }
            }

            onClose();
          },
        },
      );
    } catch (error) {
      // Błąd jest obsługiwany przez hook
    }
  };

  if (!item) return null;

  const currencySymbol = getCurrencySymbol(item.currency?.currency || "");

  return createPortal(
    <FullscreenModal isOpen={isOpen} onClose={onClose}>
      <div className="close-asset-modal">
        <h2 className="fullscreen-modal__header">
          Sprzedaż dzieła sztuki "{item.name || ""}"
        </h2>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formik) => (
            <Form className="close-asset-modal__form">
              <div className="close-asset-modal__fields">
                <div className="close-asset-modal__field">
                  <Input
                    type="number"
                    name="salePrice"
                    label="Cena sprzedaży"
                    precision={2}
                    data-suffix={currencySymbol}
                  />
                </div>
                <div className="close-asset-modal__field">
                  <Field
                    type="date"
                    name="closedAt"
                    label="Data sprzedaży"
                    component={CalendarInput}
                    errors={formik.errors}
                  />
                </div>
                <div className="close-asset-modal__field">
                  <Field
                    label="Umowa"
                    name="contractFile"
                    id="contractFile"
                    as={DropFileInput}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    optional
                  />
                </div>
              </div>
              <div className="close-asset-modal__actions">
                <Button
                  type="button"
                  onClick={onClose}
                  label="Anuluj"
                  className="button--stroke button--rounded"
                />
                <Button
                  type="submit"
                  onClick={() => {}}
                  label={isPending ? "Zapisywanie..." : "Potwierdź"}
                  className="button--rounded button--black"
                  isLoading={isPending}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </FullscreenModal>,
    document.body,
  );
};

export default CloseAssetModal;
